(() => {
  const milestones = [
    [0, "No error-corrected quantum computer", "Only research experiments. Nothing in your digital life is at risk."],
    [10, "100 logical qubits", "Scientists can run small algorithms reliably. Mostly academic demonstrations."],
    [20, "Shor breaks tiny demo keys", "Quantum computers can crack toy encryption used only in research labs."],
    [30, "64-bit RSA/ECC", "Far below anything protecting your accounts or money."],
    [40, "128-bit RSA/ECC", "Major scientific milestone. Governments accelerate migration. Consumer accounts remain safe."],
    [50, "512-bit RSA", "Old legacy encryption starts becoming vulnerable. Modern banking and websites remain secure."],
    [60, "1024-bit RSA", "Serious warning. Some older VPNs and corporate systems become vulnerable."],
    [70, "RSA-2048", "Modern internet certificates could be broken. Major institutions should already be post-quantum."],
    [80, "ECC P-256", "Cryptography comparable to Bitcoin signatures can now be attacked."],
    [90, "Exposed Bitcoin public keys", "Old Bitcoin wallets with revealed public keys become vulnerable if not migrated."],
    [100, "Large-scale quantum cryptanalysis", "Most classical public-key cryptography is obsolete unless replaced."]
  ];

  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
  const scoreToAngle = (score) => -180 + (clamp(score, 0, 100) / 100) * 180;
  const scoreToSvgAngle = (score) => scoreToAngle(score) + 90;
  const polar = (cx, cy, radius, angleDegrees) => {
    const angle = (angleDegrees - 90) * Math.PI / 180;
    return {
      x: cx + radius * Math.cos(angle),
      y: cy + radius * Math.sin(angle)
    };
  };

  const arcPath = (cx, cy, radius, start, end) => {
    const startPoint = polar(cx, cy, radius, end);
    const endPoint = polar(cx, cy, radius, start);
    const largeArcFlag = end - start <= 180 ? "0" : "1";
    return `M ${startPoint.x} ${startPoint.y} A ${radius} ${radius} 0 ${largeArcFlag} 0 ${endPoint.x} ${endPoint.y}`;
  };

  const setNeedle = (needle, cx, cy, length, score) => {
    const point = polar(cx, cy, length, scoreToSvgAngle(score));
    needle.setAttribute("x1", cx);
    needle.setAttribute("y1", cy);
    needle.setAttribute("x2", point.x);
    needle.setAttribute("y2", point.y);
  };

  async function loadData(url) {
    const response = await fetch(url, { cache: "no-store" });
    if (!response.ok) throw new Error(`Unable to load QRI index data: ${response.status}`);
    return response.json();
  }

  function renderDial(root, data) {
    const svg = root.querySelector(".qri-dial-svg");
    const needle = root.querySelector(".qri-needle");
    const tooltip = root.querySelector(".qri-tooltip");
    const score = Number(data.current_score || 12);
    const cx = 260;
    const cy = 260;
    const radius = 205;

    const setText = (selector, value) => {
      document.querySelectorAll(selector).forEach((node) => {
        node.textContent = value;
      });
    };

    setText("[data-qri-score]", score);
    setText("[data-qri-current-level]", score);
    setText("[data-qri-status]", data.status || "LOW");
    setText("[data-qri-bitcoin-status]", data.bitcoin_status || "SAFE");
    setText("[data-qri-updated]", data.last_updated || "");
    setText("[data-qri-freshness]", data.freshness || "Updated 3 hours ago");

    const zones = [
      [0, 30, "qri-zone-green"],
      [30, 60, "qri-zone-yellow"],
      [60, 80, "qri-zone-orange"],
      [80, 100, "qri-zone-red"]
    ];

    zones.forEach(([start, end, className]) => {
      const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
      path.setAttribute("d", arcPath(cx, cy, radius, scoreToSvgAngle(start), scoreToSvgAngle(end)));
      path.setAttribute("fill", "none");
      path.setAttribute("stroke-width", "24");
      path.setAttribute("stroke-linecap", "round");
      path.setAttribute("class", className);
      svg.querySelector(".qri-zones").append(path);
    });

    milestones.forEach(([level, label, meaning]) => {
      const angle = scoreToAngle(level);
      const inner = polar(cx, cy, radius - 28, scoreToSvgAngle(level));
      const outer = polar(cx, cy, radius + 4, scoreToSvgAngle(level));
      const text = polar(cx, cy, radius + 34, scoreToSvgAngle(level));
      const dot = polar(cx, cy, radius, scoreToSvgAngle(level));

      const line = document.createElementNS("http://www.w3.org/2000/svg", "line");
      line.setAttribute("x1", inner.x);
      line.setAttribute("y1", inner.y);
      line.setAttribute("x2", outer.x);
      line.setAttribute("y2", outer.y);
      line.setAttribute("class", "qri-tick-line");
      svg.querySelector(".qri-ticks").append(line);

      const tickLabel = document.createElementNS("http://www.w3.org/2000/svg", "text");
      tickLabel.setAttribute("x", text.x);
      tickLabel.setAttribute("y", text.y);
      tickLabel.setAttribute("text-anchor", "middle");
      tickLabel.setAttribute("dominant-baseline", "middle");
      tickLabel.setAttribute("class", "qri-tick-label");
      tickLabel.textContent = level;
      svg.querySelector(".qri-ticks").append(tickLabel);

      const link = document.createElementNS("http://www.w3.org/2000/svg", "a");
      link.setAttribute("href", `/pages/level-${level}`);
      link.setAttribute("class", "qri-milestone-dot");
      link.setAttribute("tabindex", "0");
      link.setAttribute("aria-label", `Level ${level}: ${label}. ${meaning}`);
      link.dataset.label = label;
      link.dataset.level = level;
      link.dataset.meaning = meaning;

      const circle = document.createElementNS("http://www.w3.org/2000/svg", "circle");
      circle.setAttribute("cx", dot.x);
      circle.setAttribute("cy", dot.y);
      circle.setAttribute("r", "7");
      link.append(circle);
      svg.querySelector(".qri-dots").append(link);
    });

    const displayNeedleScore = clamp(Number(data.needle_score ?? score), 4, 14);
    let startedAt = null;
    const animateNeedle = (time) => {
      if (!startedAt) startedAt = time;
      const elapsed = (time - startedAt) / 1000;
      const tremor = Math.sin(elapsed * 2.1) * 0.75 + Math.sin(elapsed * 4.7) * 0.28;
      setNeedle(needle, cx, cy, 168, displayNeedleScore + tremor);
      needle.classList.add("is-live");
      window.requestAnimationFrame(animateNeedle);
    };
    window.requestAnimationFrame(animateNeedle);

    const showTooltip = (event, target) => {
      tooltip.innerHTML = `<strong>Level ${target.dataset.level}: ${target.dataset.label}</strong>${target.dataset.meaning}`;
      tooltip.classList.add("is-visible");
      const rootRect = root.getBoundingClientRect();
      const eventX = event.clientX || rootRect.left + rootRect.width / 2;
      const eventY = event.clientY || rootRect.top + 180;
      tooltip.style.left = `${clamp(eventX - rootRect.left + 12, 8, rootRect.width - 292)}px`;
      tooltip.style.top = `${clamp(eventY - rootRect.top + 12, 8, rootRect.height - 120)}px`;
    };

    const hideTooltip = () => tooltip.classList.remove("is-visible");

    root.querySelectorAll(".qri-milestone-dot").forEach((dot) => {
      dot.addEventListener("mouseenter", (event) => showTooltip(event, dot));
      dot.addEventListener("mousemove", (event) => showTooltip(event, dot));
      dot.addEventListener("mouseleave", hideTooltip);
      dot.addEventListener("focus", (event) => showTooltip(event, dot));
      dot.addEventListener("blur", hideTooltip);
    });
  }

  function hydrateGauges(data) {
    document.querySelectorAll("[data-qri-json]").forEach((node) => {
      const path = node.dataset.qriJson.split(".");
      const value = path.reduce((current, key) => current && current[key], data);
      if (value !== undefined && value !== null) node.textContent = value;
    });
  }

  function setupEmbedTools(root) {
    const toggle = root.querySelector("[data-qri-embed-toggle]");
    const panel = root.querySelector("#qri-embed-panel");
    const code = root.querySelector("[data-qri-embed-code]");
    const copy = root.querySelector("[data-qri-copy-embed]");
    const status = root.querySelector("[data-qri-copy-status]");

    if (!toggle || !panel || !code) return;

    toggle.addEventListener("click", () => {
      const willOpen = panel.hidden;
      panel.hidden = !willOpen;
      toggle.setAttribute("aria-expanded", String(willOpen));
      if (willOpen) code.focus();
    });

    copy?.addEventListener("click", async () => {
      try {
        await navigator.clipboard.writeText(code.value);
        copy.textContent = "Copied";
        if (status) status.textContent = "Embed code copied.";
      } catch {
        code.select();
        document.execCommand("copy");
        copy.textContent = "Copied";
        if (status) status.textContent = "Embed code selected and copied.";
      }

      window.setTimeout(() => {
        copy.textContent = "Copy Code";
        if (status) status.textContent = "";
      }, 1800);
    });
  }

  document.addEventListener("DOMContentLoaded", async () => {
    const root = document.querySelector("[data-qri-threat-dial]");
    if (!root) return;

    setupEmbedTools(root.closest(".qri-shell") || document);

    try {
      const data = await loadData(root.dataset.dataUrl);
      renderDial(root, data);
      hydrateGauges(data);
    } catch (error) {
      root.dataset.error = "true";
      console.error(error);
    }
  });
})();
